import React from 'react';
import { ShieldCheck, Zap, Users, TrendingUp, HelpCircle, Code, Briefcase, Award } from 'lucide-react';
import { motion } from 'framer-motion';

const FeatureGrid = () => {
  const features = [
    {
      icon: <Users className="w-8 h-8 text-blue-600" />,
      title: "Top 1% Talent",
      description: "Rigorous vetting process ensures you only work with the best engineers.",
      size: "large", // spans 2 columns
      color: "bg-blue-50"
    },
    {
      icon: <Zap className="w-8 h-8 text-amber-500" />,
      title: "Rapid Deployment",
      description: "Get your team up and running in days, not months.",
      size: "small",
      color: "bg-amber-50"
    },
    {
      icon: <Code className="w-8 h-8 text-purple-600" />,
      title: "Modern Tech Stack",
      description: "Expertise in AI/ML, React, Node, Python, and more.",
      size: "small",
      color: "bg-purple-50"
    },
    {
      icon: <Briefcase className="w-8 h-8 text-green-600" />,
      title: "Flexible Engagement",
      description: "Hourly, monthly, or fixed-price models to suit your needs.",
      size: "large", // spans 2 columns to fix layout
      color: "bg-green-50"
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-red-500" />,
      title: "Managed Delivery",
      description: "We handle performance management and delivery oversight.",
      size: "small",
      color: "bg-red-50"
    },
    {
      icon: <Award className="w-8 h-8 text-indigo-500" />,
      title: "Proven Track Record",
      description: "Delivering success for startups and enterprises alike.",
      size: "small",
      color: "bg-indigo-50"
    }
  ];

  return (
    <section id="approach" className="py-24 bg-white relative">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-white pointer-events-none"></div>
      <div className="container mx-auto px-4 max-w-6xl relative z-10">
        <div className="text-center mb-16">
          <span className="bg-green-100 text-green-700 text-sm font-bold px-4 py-2 rounded-full uppercase tracking-wider mb-6 inline-block">
            Why Easy Hiring
          </span>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Built for High-Growth Teams</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            We combine the speed of contracting with the reliability of an in-house team.
          </p>
        </div>

        {/* 
           Layout Logic:
           Row 1: Large (2 cols) + Small (1 col) = 3 cols
           Row 2: Small (1 col) + Large (2 cols) = 3 cols
           Row 3: Small (1 col) + Small (1 col) ... wait, we have 6 items.
           Let's reorder for visual balance:
           Item 1: Large
           Item 2: Small
           -- Row Break --
           Item 3: Small
           Item 4: Large
           -- Row Break --
           Item 5: Small
           Item 6: Small (This would leave a gap if grid is 3 cols)
           
           Better Layout for 6 items in 3-col grid:
           L S
           S L
           S S S (needs 7 items or change sizes)
           
           Let's try a standard 3-col grid where some span 2.
           Total slots = 6 items.
           If we want full rows, we need items to sum to multiples of 3.
           Current sizes: L(2), S(1), S(1), L(2), S(1), S(1).
           Total span = 2+1+1+2+1+1 = 8.  8 is not divisible by 3.
           We need 9 spans for 3 rows.
           Let's make one more item Large or add a spacer?
           Or simpler: Just make them all 1x1 or specific pattern.
           
           Let's change "Modern Tech Stack" to Large to make it 9 spans.
           Row 1: Top Talent (2), Rapid Deployment (1) -> 3
           Row 2: Flexible Engagement (2), Modern Tech Stack (1) -> 3
           Row 3: Managed Delivery (1), Proven Track Record (1) -> Gap?
           
           Wait, user said: "first row has 2 cards (2x, 1x), second row has both 1x width cards, third row as 2x and 1x width cards. in second row there is empty space because of this."
           
           Let's fix it by ensuring we fill the rows.
           Let's do:
           Row 1: Large (2), Small (1)
           Row 2: Small (1), Large (2)
           Row 3: Small (1), Small (1), Small (1) -> We need 7 items?
           
           Actually, let's keep 6 items and just make it symmetric.
           3 columns.
           Row 1: [Large (span 2)] [Small]
           Row 2: [Small] [Large (span 2)]
           Row 3: [Small] [Small] [Small] -> need 3rd small.
           
           Or simpler: Just use a 2-column grid on mobile/tablet and 3-column on desktop, but make all cards equal size? No, Bento grid is requested.
           
           Let's try:
           Item 1: Large (2)
           Item 2: Small (1)
           Item 3: Small (1)
           Item 4: Large (2)
           Item 5: Small (1)
           Item 6: Large (2) -> Total 9 spans.
           
           Let's adjust sizing in the array below.
        */}
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Item 1: Large (2 cols) */}
          <FeatureCard feature={features[0]} className="md:col-span-2" />
          
          {/* Item 2: Small (1 col) */}
          <FeatureCard feature={features[1]} />
          
          {/* Item 3: Small (1 col) */}
          <FeatureCard feature={features[2]} />
          
          {/* Item 4: Large (2 cols) */}
          <FeatureCard feature={features[3]} className="md:col-span-2" />
          
           {/* Item 5: Small (1 col) */}
           <FeatureCard feature={features[4]} />
           
           {/* Item 6: Large (2 cols) - Changing last small to large to fill grid */}
           <FeatureCard feature={features[5]} className="md:col-span-2" />
        </div>
      </div>
    </section>
  );
};

const FeatureCard = ({ feature, className = "" }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    className={`group bg-white rounded-3xl p-8 border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col justify-between gap-6 ${className}`}
  >
    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 ${feature.color}`}>
      {feature.icon}
    </div>
    <div>
      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">{feature.title}</h3>
      <p className="text-gray-600 leading-relaxed">{feature.description}</p>
    </div>
  </motion.div>
);

export default FeatureGrid;
